import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { VideoFeatures, ThumbnailOverlay } from '@/store/videoFeatures/types';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { InitialThumbnailTab } from './thumbnails/InitialThumbnailTab';
import { PauseThumbnailTab } from './thumbnails/PauseThumbnailTab';
import { EndThumbnailTab } from './thumbnails/EndThumbnailTab';
import { useToast } from '@/hooks/use-toast';
import { ThumbnailConfigProps, ThumbnailType } from './thumbnails/types';
import { Image } from 'lucide-react';
import { motion } from 'framer-motion';
import { videoService } from '@/services/videoService';
import { useVideoFeaturesStore } from '@/store/videoFeatures/store';

export const ThumbnailConfig: React.FC<ThumbnailConfigProps> = ({
  features,
  setFeature,
}) => {
  useLanguage();
  const [activeTab, setActiveTab] = useState('initial');
  const { toast } = useToast();
  const clearTempFile = useVideoFeaturesStore((state) => state.clearTempFile);

  const handleInitialThumbnailUpload = (file: File) => {
    const imageUrl = URL.createObjectURL(file);
    setFeature('initialThumbnail', {
      enabled: true,
      imageUrl
    } as ThumbnailOverlay);

    toast({
      title: "✅ Thumbnail adicionada com sucesso",
      description: "A thumbnail de início foi configurada"
    });
  };

  const handlePauseThumbnailUpload = (file: File) => {
    const imageUrl = URL.createObjectURL(file);
    setFeature('pauseThumbnail', {
      enabled: true,
      imageUrl,
      actionButton: features.pauseThumbnail?.actionButton || {
        text: "Continue Assistindo",
        link: '',
        size: 'medium',
        position: 'bottom-center',
        colors: {
          background: '#3b82f6',
          text: '#ffffff',
          hover: '#1F6BFF'
        }
      }
    } as ThumbnailOverlay);

    toast({
      title: translateInterfaceText("✅ Thumbnail adicionada com sucesso"),
      description: translateInterfaceText("Configure o botão de ação se desejar")
    });
  };

  const handleEndThumbnailUpload = (file: File) => {
    const imageUrl = URL.createObjectURL(file);
    setFeature('endThumbnail', {
      enabled: true,
      imageUrl
    } as ThumbnailOverlay);

    toast({
      title: translateInterfaceText("✅ Thumbnail adicionada com sucesso"),
      description: translateInterfaceText("A thumbnail de final foi configurada")
    });
  };

  const updatePauseThumbnailButton = (property: string, value: any) => {
    if (!features.pauseThumbnail) return;

    const pauseThumbnail = features.pauseThumbnail as ThumbnailOverlay;
    const actionButton = pauseThumbnail.actionButton as any;

    if (property.includes('.')) {
      const [parentProp, childProp] = property.split('.');

      setFeature('pauseThumbnail', {
        ...pauseThumbnail,
        actionButton: {
          ...actionButton,
          [parentProp]: {
            ...(actionButton[parentProp] || {}),
            [childProp]: value
          }
        }
      } as ThumbnailOverlay);
    } else {
      setFeature('pauseThumbnail', {
        ...pauseThumbnail,
        actionButton: {
          ...actionButton,
          [property]: value
        }
      } as ThumbnailOverlay);
    }
  };

  const removeThumbnail = async (type: ThumbnailType) => {
    const videoId = features.videoId;
    if (!videoId) {
      toast({
        title: translateInterfaceText("Erro"),
        description: translateInterfaceText("ID do vídeo não encontrado"),
        variant: "destructive"
      });
      return;
    }

    try {
      // Call appropriate API based on thumbnail type
      if (type === 'initialThumbnail') {
        await videoService.removeThumbnailStart(videoId);
        clearTempFile('initialThumbnailFile');
      } else if (type === 'pauseThumbnail') {
        await videoService.removeThumbnailPause(videoId);
        clearTempFile('pauseThumbnailFile');
      } else if (type === 'endThumbnail') {
        await videoService.removeThumbnailEnd(videoId);
        clearTempFile('endThumbnailFile');
      }

      // Update local state
      setFeature(type, {
        enabled: false,
        imageUrl: ''
      } as ThumbnailOverlay);

      toast({
        title: translateInterfaceText("Thumbnail removida"),
        description: translateInterfaceText("A thumbnail foi removida com sucesso")
      });
    } catch (error) {
      console.error('Error removing thumbnail:', error);
      toast({
        title: translateInterfaceText("Erro ao remover thumbnail"),
        description: translateInterfaceText("Ocorreu um erro ao remover a thumbnail"),
        variant: "destructive"
      });
    }
  };

  const updatePauseThumbnailMode = (mode: 'always' | 'timeRange') => {
    if (!features.pauseThumbnail) return;

    setFeature('pauseThumbnail', {
      ...features.pauseThumbnail,
      pauseMode: mode,
      timeRange: mode === 'timeRange'
        ? features.pauseThumbnail.timeRange || { start: '' }
        : features.pauseThumbnail.timeRange
    } as ThumbnailOverlay);

    toast({
      title: translateInterfaceText("Modo atualizado"),
      description: translateInterfaceText("Thumbnail será exibida {value0}", { value0: (mode === 'always' ? 'sempre que pausar' : 'pausando automaticamente no tempo definido') })
    });
  };

  const updateTimeRange = (start: string) => {
    if (!features.pauseThumbnail) return;

    setFeature('pauseThumbnail', {
      ...features.pauseThumbnail,
      timeRange: { start }
    } as ThumbnailOverlay);
  };

  const toggleButtonEnabled = (
    type: 'pauseThumbnail',
    enabled: boolean
  ) => {
    if (!features[type]) return;

    const thumbnail = features[type] as ThumbnailOverlay;

    const actionButton = enabled ? {
      text: "Continue Assistindo",
      link: '',
      size: 'medium',
      position: 'bottom-center',
      colors: {
        background: '#3b82f6',
        text: '#ffffff',
        hover: '#1F6BFF'
      }
    } : undefined;

    setFeature(type, {
      ...thumbnail,
      actionButton
    } as ThumbnailOverlay);

    if (enabled) {
      toast({
        title: translateInterfaceText("Botão adicionado"),
        description: translateInterfaceText("Configure o botão e visualize no preview")
      });
    } else {
      toast({
        title: translateInterfaceText("Botão removido"),
        description: translateInterfaceText("O botão foi removido do thumbnail de pausa")
      });
    }
  };

  return (
    <div className="space-y-6 py-4 px-6">
      {/* Header Card sem switch - mantém estrutura visual do HeroCard */}
      <div className="flex items-center gap-3 rounded-[12px] p-3.5 bg-white border border-[#E4E7EC] transition-all duration-200 hover:border-[#D0D5DD]">
        {/* Icon Wrapper */}
        <div className="flex-shrink-0 w-9 h-9 rounded-[10px] flex items-center justify-center bg-[#EEF4FF] text-[#1D4FFF]">
          <div className="w-5 h-5">
            <Image className="w-full h-full" strokeWidth={2} />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h4 className="text-[14px] font-semibold text-[#1A1A1A] leading-tight truncate">
            {translateInterfaceText("Thumbnails do Vídeo")}</h4>
          <p className="text-[12px] font-normal text-[#6B7280] leading-snug mt-1 line-clamp-2">
            {translateInterfaceText("Defina imagens estratégicas para reforçar atenção, retenção e conversão em momentos específicos do vídeo.")}</p>
        </div>
      </div>

      {/* Subtexto estratégico */}
      <p className="text-[11px] text-[#6B7280] text-center -mt-2">
        {translateInterfaceText("Início, pausa e final podem ter imagens diferentes.")}</p>

      {/* Tabs de Thumbnails - sempre visível */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
        className="space-y-4"
      >
        <Tabs defaultValue="initial" value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full grid grid-cols-3 mb-4 bg-[#F1F5F9] p-1 rounded-lg">
            <TabsTrigger
              value="initial"
              className="rounded-lg data-[state=active]:bg-[#1D4FFF] data-[state=active]:text-white data-[state=active]:font-semibold data-[state=active]:shadow-sm transition-colors text-[12px]"
            >
              {translateInterfaceText("Início")}</TabsTrigger>
            <TabsTrigger
              value="pause"
              className="rounded-lg data-[state=active]:bg-[#1D4FFF] data-[state=active]:text-white data-[state=active]:font-semibold data-[state=active]:shadow-sm transition-colors text-[12px]"
            >
              {translateInterfaceText("Pausa")}</TabsTrigger>
            <TabsTrigger
              value="end"
              className="rounded-lg data-[state=active]:bg-[#1D4FFF] data-[state=active]:text-white data-[state=active]:font-semibold data-[state=active]:shadow-sm transition-colors text-[12px]"
            >
              {translateInterfaceText("Final")}</TabsTrigger>
          </TabsList>

          <TabsContent value="initial">
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              <InitialThumbnailTab
                features={features}
                handleInitialThumbnailUpload={handleInitialThumbnailUpload}
                removeThumbnail={removeThumbnail}
              />
            </motion.div>
          </TabsContent>

          <TabsContent value="pause">
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              <PauseThumbnailTab
                features={features}
                handlePauseThumbnailUpload={handlePauseThumbnailUpload}
                removeThumbnail={removeThumbnail}
                toggleButtonEnabled={toggleButtonEnabled}
                updatePauseThumbnailButton={updatePauseThumbnailButton}
                updatePauseThumbnailMode={updatePauseThumbnailMode}
                updatePauseAt={updateTimeRange}
              />
            </motion.div>
          </TabsContent>

          <TabsContent value="end">
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              <EndThumbnailTab
                features={features}
                handleEndThumbnailUpload={handleEndThumbnailUpload}
                removeThumbnail={removeThumbnail}
              />
            </motion.div>
          </TabsContent>
        </Tabs>
      </motion.div>
    </div>
  );
};
